export type InGridImageConfig = {
  type: "image_ingrid"
  position: number
  desktopWidth: 1 | 2
  desktopHeight: 1 | 2
  mobileWidth: 1 | 2
  mobileHeight: 1 | 2
  showOnAllPages: boolean
  layout: "overlay" | "stacked"
  tileHeight: "product_image" | "product_card"
  image: string
  imageLink: string
  altText: string
  overlayImage: string
  overlayImagePosition: "above_text" | "below_text"
  eyebrow: string
  /** Hex for the eyebrow label; empty if unset or invalid. */
  eyebrowColor: string
  headline: string
  /** Hex for the headline; empty if unset or invalid. */
  headlineColor: string
  copy: string
  /** Hex for the body copy; empty if unset or invalid. */
  copyColor: string
  ctaText: string
  ctaLink: string
  /** Hex for the CTA (e.g. `#fff`); empty if unset or invalid. */
  ctaColor: string
  /** Hex for primary CTA label; empty uses theme default (`--color-eb-text`). */
  ctaPrimaryTextColor: string
  ctaStyle: "primary" | "secondary" | "link"
  textColor: "light" | "dark"
  textAlignH: "left" | "center" | "right"
  textAlignV: "top" | "center" | "bottom"
  eyebrowSpacing: number
  headlineSpacing: number
  copySpacing: number
  overlayImageSpacing: number
  ctaSpacing: number
  gradientEnabled: boolean
  gradientDirection: "top_to_bottom" | "bottom_to_top"
  gradientColor: "light" | "dark"
}

export type InGridCopyBlockConfig = {
  type: "copy_block_ingrid"
  position: number
  desktopWidth: 1 | 2
  desktopHeight: 1 | 2
  mobileWidth: 1 | 2
  mobileHeight: 1 | 2
  showOnAllPages: boolean
  backgroundImage: string
  backgroundColor: string
  textColor: "light" | "dark"
  blockLink: string
  altText: string
  eyebrow: string
  /** Hex for the eyebrow label; empty if unset or invalid. */
  eyebrowColor: string
  headline: string
  /** Hex for the headline; empty if unset or invalid. */
  headlineColor: string
  copy: string
  /** Hex for the body copy; empty if unset or invalid. */
  copyColor: string
  ctaText: string
  ctaLink: string
  /** Hex for the CTA (e.g. `#fff`); empty if unset or invalid. */
  ctaColor: string
  /** Hex for primary CTA label; empty uses theme default (`--color-eb-text`). */
  ctaPrimaryTextColor: string
  ctaStyle: "primary" | "secondary" | "link"
  contentPosition: string
}

export type InGridVideoConfig = {
  type: "video_ingrid"
  position: number
  desktopWidth: 1 | 2
  desktopHeight: 1 | 2
  mobileWidth: 1 | 2
  mobileHeight: 1 | 2
  showOnAllPages: boolean
  videoUrl: string
  altText: string
  eyebrow: string
  /** Hex for the eyebrow label; empty if unset or invalid. */
  eyebrowColor: string
  headline: string
  /** Hex for the headline; empty if unset or invalid. */
  headlineColor: string
  copy: string
  /** Hex for the body copy; empty if unset or invalid. */
  copyColor: string
  ctaText: string
  ctaLink: string
  /** Hex for the CTA (e.g. `#fff`); empty if unset or invalid. */
  ctaColor: string
  /** Hex for primary CTA label; empty uses theme default (`--color-eb-text`). */
  ctaPrimaryTextColor: string
  ctaStyle: "primary" | "secondary" | "link"
  textColor: "light" | "dark"
  contentPosition: string
}

export type InGridConfig = InGridImageConfig | InGridCopyBlockConfig | InGridVideoConfig

const VALID_TYPES = ["image_ingrid", "copy_block_ingrid", "video_ingrid"] as const
const VALID_LAYOUTS = ["overlay", "stacked"] as const
const VALID_CTA_STYLES = ["primary", "secondary", "link"] as const

const SAFE_PROTOCOLS = ["https:", "http:"]

function warnInvalidConfig(reason: string, payload?: unknown): void {
  console.warn("[nosto-ingrid] Dropping config item:", reason, payload)
}

/** Theme JSON may emit grid spans as numbers or strings ("1" / "2"). */
function parseGridSpan(value: unknown): 1 | 2 | null {
  if (value === 1 || value === 2) return value
  if (value === "1" || value === "2") return Number(value) as 1 | 2
  if (typeof value === "number" && Number.isInteger(value) && (value === 1 || value === 2)) return value
  return null
}

function normalizeGridSpans(item: Record<string, unknown>): void {
  for (const key of ["desktopWidth", "desktopHeight", "mobileWidth", "mobileHeight"] as const) {
    const parsed = parseGridSpan(item[key])
    if (parsed !== null) item[key] = parsed
  }
}

const HEX_COLOR_RE = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i

/** Allow only safe hex colors for inline styles (no `url()`, expressions, etc.). */
function sanitizeInGridHexColor(value: unknown): string {
  if (typeof value !== "string") return ""
  const v = value.trim()
  return HEX_COLOR_RE.test(v) ? v : ""
}

function sanitizeUrl(url: string, allowedProtocols = SAFE_PROTOCOLS): string {
  if (!url) return ""
  if (url.startsWith("/")) return url

  try {
    const parsed = new URL(url, window.location.origin)
    if (allowedProtocols.includes(parsed.protocol)) return url
    return ""
  } catch {
    return ""
  }
}

function validateCommon(item: Record<string, unknown>): boolean {
  if (!(VALID_TYPES as readonly string[]).includes(item.type as string)) return false
  if (typeof item.position !== "number" || item.position < 1 || !Number.isInteger(item.position)) return false
  normalizeGridSpans(item)
  const { desktopWidth: dw, desktopHeight: dh, mobileWidth: mw, mobileHeight: mh } = item
  if (dw !== 1 && dw !== 2) return false
  if (dh !== 1 && dh !== 2) return false
  if (mw !== 1 && mw !== 2) return false
  if (mh !== 1 && mh !== 2) return false
  if (!(VALID_CTA_STYLES as readonly string[]).includes(item.ctaStyle as string)) return false
  return true
}

function commonFields(item: Record<string, unknown>) {
  return {
    position: item.position as number,
    desktopWidth: item.desktopWidth as 1 | 2,
    desktopHeight: item.desktopHeight as 1 | 2,
    mobileWidth: item.mobileWidth as 1 | 2,
    mobileHeight: item.mobileHeight as 1 | 2,
    showOnAllPages: item.showOnAllPages === true,
    altText: String(item.altText ?? ""),
    eyebrow: String(item.eyebrow ?? ""),
    headline: String(item.headline ?? ""),
    copy: String(item.copy ?? ""),
    ctaText: String(item.ctaText ?? ""),
    ctaLink: sanitizeUrl(String(item.ctaLink ?? "")),
    ctaStyle: item.ctaStyle as "primary" | "secondary" | "link"
  }
}

function validateItem(raw: unknown): InGridConfig | null {
  if (typeof raw !== "object" || raw === null) {
    warnInvalidConfig("item is not an object", raw)
    return null
  }
  const item = raw as Record<string, unknown>
  if (!validateCommon(item)) {
    warnInvalidConfig("failed common validation", item)
    return null
  }

  switch (item.type) {
    case "image_ingrid": {
      if (!(VALID_LAYOUTS as readonly string[]).includes(item.layout as string)) {
        warnInvalidConfig("invalid image_ingrid layout", item)
        return null
      }
      const common = commonFields(item)
      return {
        type: "image_ingrid",
        position: common.position,
        desktopWidth: common.desktopWidth,
        desktopHeight: common.desktopHeight,
        mobileWidth: common.mobileWidth,
        mobileHeight: common.mobileHeight,
        showOnAllPages: common.showOnAllPages,
        layout: item.layout as "overlay" | "stacked",
        tileHeight: (item.tileHeight === "product_image" ? "product_image" : "product_card") as
          | "product_image"
          | "product_card",
        image: sanitizeUrl(String(item.image ?? ""), ["https:"]),
        imageLink: sanitizeUrl(String(item.imageLink ?? "")),
        altText: common.altText,
        overlayImage: sanitizeUrl(String(item.overlayImage ?? ""), ["https:"]),
        overlayImagePosition: (item.overlayImagePosition === "below_text" ? "below_text" : "above_text") as
          | "above_text"
          | "below_text",
        eyebrow: common.eyebrow,
        eyebrowColor: sanitizeInGridHexColor(item.eyebrowColor),
        headline: common.headline,
        headlineColor: sanitizeInGridHexColor(item.headlineColor),
        copy: String(item.copy ?? ""),
        copyColor: sanitizeInGridHexColor(item.copyColor),
        ctaText: common.ctaText,
        ctaLink: common.ctaLink,
        ctaColor: sanitizeInGridHexColor(item.ctaColor),
        ctaPrimaryTextColor: sanitizeInGridHexColor(item.ctaPrimaryTextColor),
        ctaStyle: common.ctaStyle,
        textColor: (item.textColor === "dark" ? "dark" : "light") as "light" | "dark",
        textAlignH: (["left", "center", "right"].includes(item.textAlignH as string) ? item.textAlignH : "left") as
          | "left"
          | "center"
          | "right",
        textAlignV: (["top", "center", "bottom"].includes(item.textAlignV as string) ? item.textAlignV : "bottom") as
          | "top"
          | "center"
          | "bottom",
        eyebrowSpacing: typeof item.eyebrowSpacing === "number" ? item.eyebrowSpacing : 0,
        headlineSpacing: typeof item.headlineSpacing === "number" ? item.headlineSpacing : 4,
        copySpacing: typeof item.copySpacing === "number" ? item.copySpacing : 4,
        overlayImageSpacing: typeof item.overlayImageSpacing === "number" ? item.overlayImageSpacing : 4,
        ctaSpacing: typeof item.ctaSpacing === "number" ? item.ctaSpacing : 8,
        gradientEnabled: item.gradientEnabled === true,
        gradientDirection: (item.gradientDirection === "top_to_bottom" ? "top_to_bottom" : "bottom_to_top") as
          | "top_to_bottom"
          | "bottom_to_top",
        gradientColor: (item.gradientColor === "light" ? "light" : "dark") as "light" | "dark"
      }
    }
    case "copy_block_ingrid": {
      const copyCommon = commonFields(item)
      return {
        type: "copy_block_ingrid",
        ...copyCommon,
        eyebrowColor: sanitizeInGridHexColor(item.eyebrowColor),
        headlineColor: sanitizeInGridHexColor(item.headlineColor),
        copyColor: sanitizeInGridHexColor(item.copyColor),
        ctaColor: sanitizeInGridHexColor(item.ctaColor),
        ctaPrimaryTextColor: sanitizeInGridHexColor(item.ctaPrimaryTextColor),
        backgroundImage: sanitizeUrl(String(item.backgroundImage ?? ""), ["https:"]),
        backgroundColor: String(item.backgroundColor ?? ""),
        textColor: (item.textColor === "light" ? "light" : "dark") as "light" | "dark",
        blockLink: sanitizeUrl(String(item.blockLink ?? "")),
        contentPosition: String(item.contentPosition ?? "")
      }
    }
    case "video_ingrid": {
      return {
        type: "video_ingrid",
        ...commonFields(item),
        videoUrl: sanitizeUrl(String(item.videoUrl ?? ""), ["https:"]),
        textColor: (item.textColor === "dark" ? "dark" : "light") as "light" | "dark",
        eyebrowColor: sanitizeInGridHexColor(item.eyebrowColor),
        headlineColor: sanitizeInGridHexColor(item.headlineColor),
        copyColor: sanitizeInGridHexColor(item.copyColor),
        ctaColor: sanitizeInGridHexColor(item.ctaColor),
        ctaPrimaryTextColor: sanitizeInGridHexColor(item.ctaPrimaryTextColor),
        contentPosition: String(item.contentPosition ?? "")
      }
    }
    default:
      warnInvalidConfig("unsupported block type", item)
      return null
  }
}

export function readInGridConfig(): InGridConfig[] {
  const el = document.getElementById("nosto-ingrid-config")
  if (!el?.textContent) return []
  try {
    const raw: unknown = JSON.parse(el.textContent.trim())
    if (!Array.isArray(raw)) {
      warnInvalidConfig("config root is not an array", raw)
      return []
    }

    const validated = raw.map(validateItem).filter((item): item is InGridConfig => item !== null)
    if (validated.length !== raw.length) {
      console.warn(
        `[nosto-ingrid] Parsed ${validated.length}/${raw.length} in-grid items; some were dropped by validation`
      )
    }
    return validated
  } catch (error) {
    console.warn("[nosto-ingrid] Failed to parse #nosto-ingrid-config JSON", error)
    return []
  }
}
